(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
  typeof define === 'function' && define.amd ? define(['exports'], factory) :
  (factory((global.rxFirebase = global.rxFirebase || {})));
}(this, function (exports) { 'use strict';

  /**
   * rx-firebase/sync-list.js
   */

  var meths = {
    findIndexByKey: {
      value: function value(key) {
        return this.findIndex(function (s) {
          return s.$key === key;
        });
      }
    },

    push: {
      value: function value(snapshot) {
        if (!snapshot.$prev) {
          Array.prototype.unshift.call(this, snapshot);

          return this;
        }

        var index = this.findIndexByKey(snapshot.$prev);

        Array.prototype.splice.call(this, index + 1, 0, snapshot);

        return this;
      }
    },

    remove: {
      value: function value(snapshot) {
        var index = this.findIndexByKey(snapshot.$key);

        if (index < 0) {
          return this;
        }

        Array.prototype.splice.call(this, index, 1);

        return this;
      }
    },

    update: {
      value: function value(snapshot) {
        var index = this.findIndexByKey(snapshot.$key);

        if (index < 0) {
          return this.push(snapshot);
        }

        if (this[index].$prev !== snapshot.$prev) {
          Array.prototype.splice.call(this, index, 1);

          return this.push(snapshot);
        }

        Array.prototype.splice.call(this, index, 1, snapshot);

        return this;
      }
    },

    move: {
      value: function value(snapshot) {
        return this.remove(snapshot).push(snapshot);
      }
    }
  };

  ['copyWithin', 'fill', 'pop', 'reverse', 'shift', 'sort', 'splice', 'unshift'].reduce(function (meths, name) {
    meths[name] = {
      value: function value() {
        var copy = this.slice();

        return copy[name].apply(copy, arguments);
      }
    };

    return meths;
  }, meths);

  /**
   * Create a new sync list
   *
   * @return {array}
   */
  function create() {
    var arr = [];

    Object.defineProperties(arr, meths);

    return arr;
  }

  /**
   * Extends firebase.auth.Auth and firebase.database.Query with methods returning
   * Observable instead of taking callback function.
   *
   * @param  {firebase}   firebase
   * @param  {Observable} Observable A ES stage 1 Observable extended with the
   *                                 "map", "merge" and "scan" operator.
   */
  function extend(firebase, Observable) {

    /**
     * Create an observable emitting user status change.
     *
     * Note: This is creating a cold observable; it will only start watching auth
     * when subscribing to it.
     *
     * @example
     * firebaseApp.auth().observeAuthState().subscribe(
     *   user => console.log('UID: ' + user.uid)
     * );
     *
     * @return {Observable}
     */
    firebase.auth.Auth.prototype.observeAuthState = function () {
      return new Observable(this.onAuthStateChanged.bind(this));
    };

    /**
     * Create a (cold) observable emitting changes over a firebase data reference
     * or query.
     *
     * The values are unpacked by default. Literal values are unpacked into
     * a "$value" property with toString and toJSON returning the literal the
     * "$value" property.
     *
     * Options:
     *
     * - "unpack": set to false to not unpack.
     * - "toString": set to false to not provide toString to unpacked literal
     * values.
     * - "toJSON": set to false to not provide toJSON to unpacked literal values.
     *
     * @example
     * firebaseApp.database().ref('/some/data').observe('value').subscribe(
     *   value => console.log('some data value: ' + value)
     * )
     *
     * @param   {string}     eventType "value", "child_added", "child_removed",
     *                                 "child_changed" or "child_moved".
     * @options {Object}     options   unpacking options.
     * @return  {Observable}
     */
    firebase.database.Query.prototype.observe = function (eventType, options) {
      var _this = this;

      options = Object.assign({
        toString: true,
        toJSON: true
      }, options);

      if (options.unpack === true || options.unpack === undefined) {
        options.unpack = function (snapshot) {
          return snapshot.val();
        };
      } else if (options.unpack === false) {
        options.unpack = function (snapshot) {
          return snapshot;
        };
      }

      return new Observable(function (observer) {
        var handler = function handler(snapshot, prev) {
          return observer.next(unpackSnapShot(snapshot, prev, eventType, options));
        };
        var onError = function onError(err) {
          return observer.error(err);
        };

        _this.on(eventType, handler, onError);

        return function () {
          return _this.off(eventType, handler);
        };
      });
    };

    /**
     * Create a (cold) observable emitting a firebase data reference
     * sorted array of its children snapshot.
     *
     * @example
     * const ref = firebaseApp.database().ref('/some/data');
     *
     * ref.push().setWithPriority({name: 'first'}, 1);
     * ref.push().setWithPriority({name: 'second'}, 2);
     * ref('/some/data').orderByPriority().observeChildren().subscribe(
     *   list => console.log(list);
     * );
     * // Output:
     * // [{name: 'first'}]
     * // [{name: 'first'}, {name: 'second'}]
     * //
     * // (you could use RxJS `debounce` operator to only emit the list if it's
     * // stable).
     * const otherRef = firebaseApp.database().ref('/some/other/data');
     *
     * otherRef.push().setWithPriority('first', 1);
     * otherRef.push().setWithPriority('second', 2);
     * otherRef('/some/data').orderByPriority().observeChildren().subscribe(
     *   list => console.log(list);
     * );
     * // Output:
     * // [{$value: 'first'}]
     * // [{$value: 'first'}, {$value: 'second'}]
     *
     * @param  {boolean|function} unpack Should the snapshot be unpacked to their values?
     * @return {Observable}              Emit the sorted array of children each time one is updated.
     */
    firebase.database.Query.prototype.observeChildren = function (options) {
      // Each child event will be mapped to function to edit the sync-list.
      //
      // Insert a node
      var seed = create();

      var addChild = this.observe('child_added', options).map(function (ss) {
        return function (list) {
          return list.push(ss);
        };
      });
      // Replace a node with a new ss
      var resetChild = this.observe('child_changed', options).map(function (ss) {
        return function (list) {
          return list.update(ss);
        };
      });
      // Replace a node with a new ss at a different index
      var moveChild = this.observe('child_moved', options).map(function (ss) {
        return function (list) {
          return list.move(ss);
        };
      });
      // Remove a node
      var removeChild = this.observe('child_removed', options).map(function (ss) {
        return function (list) {
          return list.remove(ss);
        };
      });

      return addChild.merge(resetChild).merge(moveChild).merge(removeChild).scan(function (list, fn) {
        return fn(list);
      }, seed).startWith(seed);
    };
  }

  function unpackSnapShot(snapShot, prev, eventType, options) {
    var val = options.unpack(snapShot);

    return Object.defineProperties(wrapValue(val, options), {
      $prev: { value: prev },
      $eventType: { value: eventType },
      $key: { value: snapShot.key },
      $ref: { value: snapShot.ref }
    });
  }

  function wrapValue(val, options) {
    if (Object.isExtensible(val)) {
      return val;
    }

    var props = {};

    if (options.toString) {
      props.toString = {
        value: function value() {
          return this.$value.toString();
        },

        writable: true
      };
    }

    if (options.toJSON) {
      props.toJSON = {
        value: function value() {
          return this.$value;
        },

        writable: true
      };
    }

    return Object.defineProperties({ $value: val }, props);
  }

  exports.extend = extend;

  Object.defineProperty(exports, '__esModule', { value: true });

}));


//////////////////
// WEBPACK FOOTER
// ./~/rx-firebase/rx-firebase.js
// module id = 1179
// module chunks = 0